import { computed, inject, Injectable, NgZone } from '@angular/core';
import { CanActivate, CanDeactivate, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Router } from '@angular/router';

import { from, Observable, of } from 'rxjs';
import { take } from 'rxjs/operators';

import { UserService } from '../services/user/user-svc.ng';
import { SnackBarService } from '@polk_county_public_schools/ngx-pcps-dialogs/snackbar';
import { Roles, RoutePaths } from './app.routes';

@Injectable({
    providedIn: 'root',
})
export class AuthenticatedGuard implements CanActivate {
    // #region Injects

    protected readonly router = inject(Router);
    protected readonly snackSvc = inject(SnackBarService);
    protected readonly userSvc = inject(UserService);

    // #endregion

    // #region Properties

    protected readonly isAuthenticated = computed(() => !!this.userSvc.user()?.AuthToken);
    protected readonly userRoles = computed(() => this.userSvc.user()?.Roles || []);

    // #endregion

    // #region Can Activate

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
        // At a minimum, only allow access if we have a valid authtoken stored for the current user.
        let routeOk = this.isAuthenticated();

        if (!routeOk) {
            this.router.navigate([`/${RoutePaths.Home}`]);
            return false;
        }

        // If there is optional data present, particularly the 'Roles' setting, we must also check to ensure that the
        // current user has a role that is in the provided list of necessary roles to use the route.

        // If the route we are trying to go to requires specific roles, ensure the current user has one of the roles needed.
        if (route.data?.['Roles']?.length) {
            // Does the list of needed roles include Roles.ALL? If so, we're good to go.
            const neededRoles: Roles[] = route.data['Roles'] || [];

            if (!neededRoles.includes(Roles.ALL)) {
                // Does the user have a role that is in the provided list?
                if (!neededRoles.some((nr) => !!this.userRoles().find((ur) => ur === nr))) {
                    // Cannot find any of the needed roles within the list of user owned roles. That is a failure.
                    routeOk = false;
                }
            }
        }

        if (!routeOk) {
            this.snackSvc.error('You are not authorized to view the requested page.');
        }

        return routeOk;
    }

    // #endregion
}
